/* QuackManGame — original arcade-style maze eater game for Credit Quack.
   Custom maze, custom "credit villain" enemies, custom rubber-duck hero.
   Canvas rendered, keyboard + on-screen D-pad, sound effects via Web Audio.
*/
import React, { useEffect, useRef, useState } from "react";
import { Play, RotateCcw, Volume2, VolumeX, Trophy, Heart, Send, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

// --- Maze layout ------------------------------------------------------------
// # wall  . dot  o power pellet  space empty  = ghost door  G ghost spawn
// P player spawn  T tunnel edge (informational — wrap handled by x-bounds)
const MAZE = [
  "###################",
  "#........#........#",
  "#o##.###.#.###.##o#",
  "#.................#",
  "#.##.#.#####.#.##.#",
  "#....#...#...#....#",
  "####.###.#.###.####",
  "   #.#       #.#   ",
  "####.# ##=## #.####",
  "T   .  #GGG#  .   T",
  "####.# ##### #.####",
  "   #.#       #.#   ",
  "####.#.#####.#.####",
  "#........#........#",
  "#.##.###.#.###.##.#",
  "#o.#.....P.....#.o#",
  "##.#.#.#####.#.#.##",
  "#....#...#...#....#",
  "#.######.#.######.#",
  "#.................#",
  "###################",
];
const COLS = 19, ROWS = 21, TILE = 22;
const W = COLS * TILE, H = ROWS * TILE;

// Credit-themed villains — each with a distinct character outfit that gets
// drawn ON TOP of the classic bell-shape ghost silhouette.
const VILLAINS = [
  { key: "debt",  name: "Debt",        color: "#ef4444", outfit: "banker"   },
  { key: "util",  name: "Utilization", color: "#f472b6", outfit: "beverly"  },
  { key: "late",  name: "Late Fee",    color: "#22d3ee", outfit: "rapper"   },
  { key: "inq",   name: "Inquiry",     color: "#f59e0b", outfit: "hipster"  },
];

// Directions
const DIRS = {
  left:  { dx: -1, dy: 0 },
  right: { dx: 1,  dy: 0 },
  up:    { dx: 0,  dy: -1 },
  down:  { dx: 0,  dy: 1 },
  none:  { dx: 0,  dy: 0 },
};
const OPP = { left: "right", right: "left", up: "down", down: "up" };

// --- Helpers ----------------------------------------------------------------
function tileAt(grid, x, y) {
  if (y < 0 || y >= ROWS) return "#";
  if (x < 0 || x >= COLS) return " "; // outside cols = tunnel space
  return grid[y][x];
}
function isBlocked(grid, x, y, allowDoor = false) {
  const c = tileAt(grid, x, y);
  if (c === "#") return true;
  if (c === "=" && !allowDoor) return true;
  return false;
}
function px2tile(px) { return Math.floor(px / TILE); }
function centerOfTile(tx, ty) { return { x: tx * TILE + TILE / 2, y: ty * TILE + TILE / 2 }; }

// --- Audio ------------------------------------------------------------------
class GameAudio {
  constructor() { this.ctx = null; this.master = null; this.enabled = true; this._unlocked = false; }
  async unlock() {
    if (this._unlocked) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.15;
      this.master.connect(this.ctx.destination);
      if (this.ctx.state === "suspended") await this.ctx.resume();
      this._unlocked = true;
    } catch {}
  }
  _play(freq, dur = 0.06, type = "square", vol = 0.5) {
    if (!this.enabled || !this._unlocked || !this.ctx) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.master);
    o.start(t); o.stop(t + dur + 0.02);
  }
  chomp(hi = false) { this._play(hi ? 900 : 640, 0.05, "square", 0.35); }
  power() { this._play(220, 0.12, "sawtooth", 0.4); setTimeout(() => this._play(440, 0.12, "sawtooth", 0.4), 90); }
  eat()   { this._play(880, 0.1, "sine", 0.5); setTimeout(() => this._play(1320, 0.14, "sine", 0.5), 70); }
  die() {
    if (!this._unlocked || !this.ctx) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(660, t);
    o.frequency.exponentialRampToValueAtTime(80, t + 0.9);
    g.gain.setValueAtTime(0.35, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1);
    o.connect(g).connect(this.master);
    o.start(t); o.stop(t + 1.1);
  }
  start() { [520, 660, 880].forEach((f, i) => setTimeout(() => this._play(f, 0.15, "square", 0.45), i * 140)); }
}

// --- Game state -------------------------------------------------------------
function makeState() {
  const grid = MAZE.map(r => r.split(""));
  // Find player spawn
  let pSpawn = { x: 9, y: 15 };
  const ghostSpawnCenter = { x: 9, y: 9 };
  for (let y = 0; y < ROWS; y++)
    for (let x = 0; x < COLS; x++)
      if (grid[y][x] === "P") { pSpawn = { x, y }; grid[y][x] = "."; }

  // Four ghost house slots. Row 9 of the maze is "#GGG#" — the interior is
  // ONLY cols 8, 9, 10. Ghost 3 was previously spawning at col 11 (a wall
  // tile) and getting permanently stuck. Pack all 4 inside the house instead.
  const HOUSE_SLOTS = [
    { dx: -1,   dy: 0   }, // col 8, row 9
    { dx: 0,    dy: 0   }, // col 9, row 9
    { dx: 1,    dy: 0   }, // col 10, row 9
    { dx: 0,    dy: 0.5 }, // col 9, slightly below center (stagger)
  ];
  const ghosts = VILLAINS.map((v, i) => {
    const slot = HOUSE_SLOTS[i] || HOUSE_SLOTS[0];
    return {
      ...v,
      x: (ghostSpawnCenter.x + slot.dx) * TILE + TILE / 2,
      y: (ghostSpawnCenter.y + slot.dy) * TILE + TILE / 2,
      dir: ["up", "down", "left", "right"][i % 4],
      scared: false,
      eaten: false,
      speed: 0.068 + i * 0.004,
      inHouse: true,
      releaseAt: i * 500, // faster stagger — all 4 released within 2s
    };
  });

  return {
    grid,
    tick: 0,
    score: 0,
    lives: 3,
    level: 1,
    status: "menu", // menu | playing | paused | dying | won | gameover
    player: {
      x: pSpawn.x * TILE + TILE / 2,
      y: pSpawn.y * TILE + TILE / 2,
      dir: "left",
      nextDir: "left",
      speed: 0.075,
      mouth: 0,
    },
    pSpawn,
    ghostSpawnCenter,
    ghosts,
    powerTimer: 0,
    dotsRemaining: 0,
    highScore: parseInt((typeof localStorage !== "undefined" && localStorage.getItem("cq_qm_high")) || "0", 10),
    dyingTimer: 0,
    startedAt: 0,
  };
}

function countDots(state) {
  let n = 0;
  for (let y = 0; y < ROWS; y++)
    for (let x = 0; x < COLS; x++)
      if (state.grid[y][x] === "." || state.grid[y][x] === "o") n++;
  return n;
}

function resetLevel(state) {
  state.grid = MAZE.map(r => r.split(""));
  for (let y = 0; y < ROWS; y++)
    for (let x = 0; x < COLS; x++)
      if (state.grid[y][x] === "P") state.grid[y][x] = ".";
  resetPositions(state);
  state.dotsRemaining = countDots(state);
  state.powerTimer = 0;
}

function resetPositions(state) {
  state.player.x = state.pSpawn.x * TILE + TILE / 2;
  state.player.y = state.pSpawn.y * TILE + TILE / 2;
  state.player.dir = "left";
  state.player.nextDir = "left";
  const slots = [
    { dx: -1, dy: 0 }, { dx: 0, dy: 0 }, { dx: 1, dy: 0 }, { dx: 0, dy: 0.5 },
  ];
  state.ghosts.forEach((g, i) => {
    const slot = slots[i] || slots[0];
    g.x = (state.ghostSpawnCenter.x + slot.dx) * TILE + TILE / 2;
    g.y = (state.ghostSpawnCenter.y + slot.dy) * TILE + TILE / 2;
    g.scared = false;
    g.eaten = false;
    g.inHouse = true;
    g.releaseAt = state.tick + i * 500;
    g.dir = ["up", "down", "left", "right"][i % 4];
  });
}

// Can the entity centered at (cx, cy) move one pixel in direction d without
// hitting a wall? Uses TILE*0.55 lookahead so from an exact tile center the
// check lands on the NEXT tile (not the current one). This was the root cause
// of ghosts getting stuck on the door tile — with TILE/2 offset, moving up
// from doorY landed on ny = doorY - TILE/2 which floor()'d back to the door.
function canMove(grid, cx, cy, dir, allowDoor = false) {
  const { dx, dy } = DIRS[dir];
  const lookahead = TILE * 0.55;
  const nx = cx + dx * lookahead;
  const ny = cy + dy * lookahead;
  const tx = Math.floor(nx / TILE);
  const ty = Math.floor(ny / TILE);
  if (tx < 0 || tx >= COLS) return true; // tunnel wrap
  return !isBlocked(grid, tx, ty, allowDoor);
}

function atTileCenter(cx, cy) {
  const rx = ((cx % TILE) + TILE) % TILE;
  const ry = ((cy % TILE) + TILE) % TILE;
  return Math.abs(rx - TILE / 2) < 1.2 && Math.abs(ry - TILE / 2) < 1.2;
}

function step(state, dt, audio) {
  state.tick += dt;
  const p = state.player;
  const g = state;

  // ---- Player movement
  // Try to switch direction if requested
  if (p.nextDir !== p.dir) {
    // Allow immediate reversal always
    if (p.nextDir === OPP[p.dir]) p.dir = p.nextDir;
    else if (atTileCenter(p.x, p.y) && canMove(g.grid, p.x, p.y, p.nextDir)) {
      // Snap to center then turn
      p.x = Math.floor(p.x / TILE) * TILE + TILE / 2;
      p.y = Math.floor(p.y / TILE) * TILE + TILE / 2;
      p.dir = p.nextDir;
    }
  }
  if (canMove(g.grid, p.x, p.y, p.dir)) {
    const { dx, dy } = DIRS[p.dir];
    p.x += dx * p.speed * dt;
    p.y += dy * p.speed * dt;
    // Tunnel wrap
    if (p.x < -TILE / 2) p.x = COLS * TILE + TILE / 2;
    if (p.x > COLS * TILE + TILE / 2) p.x = -TILE / 2;
    p.mouth = (p.mouth + dt * 0.012) % (Math.PI * 2);
  }

  // ---- Dot / pellet consumption
  const tx = Math.floor(p.x / TILE);
  const ty = Math.floor(p.y / TILE);
  if (ty >= 0 && ty < ROWS && tx >= 0 && tx < COLS) {
    const ch = g.grid[ty][tx];
    if (ch === ".") { g.grid[ty][tx] = " "; g.score += 10; g.dotsRemaining--; audio.chomp(g.tick % 200 < 100); }
    else if (ch === "o") {
      g.grid[ty][tx] = " ";
      g.score += 50;
      g.dotsRemaining--;
      g.powerTimer = 6000;
      g.ghosts.forEach(gh => { if (!gh.eaten) gh.scared = true; });
      audio.power();
    }
  }

  // ---- Power timer
  if (g.powerTimer > 0) {
    g.powerTimer -= dt;
    if (g.powerTimer <= 0) g.ghosts.forEach(gh => gh.scared = false);
  }

  // ---- Ghost movement (AI: at intersections pick direction that reduces distance to player,
  //      or increases distance when scared; never reverse unless dead-end)
  g.ghosts.forEach(gh => {
    // Release from house — first center on the door column, then travel up
    // through the door tile. Without this centering step, off-center ghosts
    // exit into a wall tile and get stuck (bug found by testing_agent).
    if (gh.inHouse) {
      if (g.tick >= gh.releaseAt) {
        const doorX = g.ghostSpawnCenter.x * TILE + TILE / 2;
        const doorY = (g.ghostSpawnCenter.y - 1) * TILE + TILE / 2;
        const centerStep = gh.speed * dt;
        if (Math.abs(gh.x - doorX) > 0.6) {
          // Slide horizontally to the door column
          gh.x += Math.sign(doorX - gh.x) * centerStep;
          if (Math.abs(gh.x - doorX) < centerStep) gh.x = doorX;
        } else {
          gh.x = doorX;
          // Climb through the door
          gh.y -= gh.speed * dt * 1.3;
          if (gh.y <= doorY) {
            gh.y = doorY;
            gh.inHouse = false;
            gh.dir = "up";
          }
        }
      }
      return;
    }
    // Eaten ghost returns to house
    if (gh.eaten) {
      const homeX = g.ghostSpawnCenter.x * TILE + TILE / 2;
      const homeY = g.ghostSpawnCenter.y * TILE + TILE / 2;
      const dxh = homeX - gh.x, dyh = homeY - gh.y;
      const dist = Math.hypot(dxh, dyh);
      if (dist < 3) { gh.eaten = false; gh.scared = false; gh.inHouse = true; gh.releaseAt = g.tick + 1200; return; }
      const speed = gh.speed * 2.4;
      gh.x += (dxh / dist) * speed * dt;
      gh.y += (dyh / dist) * speed * dt;
      return;
    }
    // Move in current direction; at tile centers, re-evaluate at intersections
    if (atTileCenter(gh.x, gh.y)) {
      gh.x = Math.floor(gh.x / TILE) * TILE + TILE / 2;
      gh.y = Math.floor(gh.y / TILE) * TILE + TILE / 2;
      const options = ["left", "right", "up", "down"].filter(d => {
        if (d === OPP[gh.dir]) return false; // no reverse
        return canMove(g.grid, gh.x, gh.y, d, false);
      });
      if (options.length === 0) {
        // Dead-end — reverse
        gh.dir = OPP[gh.dir];
      } else if (options.length === 1) {
        gh.dir = options[0];
      } else {
        // Bias toward player (or away when scared). Higher smart-bias so
        // ghosts hunt more aggressively — the previous 72% "best" felt like
        // aimless wandering during automated idle tests.
        const targetX = p.x, targetY = p.y;
        const sign = gh.scared ? -1 : 1;
        const scored = options.map(d => {
          const { dx, dy } = DIRS[d];
          const nx = gh.x + dx * TILE;
          const ny = gh.y + dy * TILE;
          const score = sign * -Math.hypot(nx - targetX, ny - targetY);
          return { d, score };
        });
        scored.sort((a, b) => b.score - a.score);
        gh.dir = Math.random() < 0.9 ? scored[0].d : options[Math.floor(Math.random() * options.length)];
      }
    }
    if (canMove(g.grid, gh.x, gh.y, gh.dir, false)) {
      const { dx, dy } = DIRS[gh.dir];
      const spd = gh.scared ? gh.speed * 0.55 : gh.speed;
      gh.x += dx * spd * dt;
      gh.y += dy * spd * dt;
    } else {
      // Wall — pick new dir next tile
    }
    // Tunnel wrap
    if (gh.x < -TILE / 2) gh.x = COLS * TILE + TILE / 2;
    if (gh.x > COLS * TILE + TILE / 2) gh.x = -TILE / 2;
  });

  // ---- Collision
  g.ghosts.forEach(gh => {
    if (gh.inHouse || gh.eaten) return;
    const d = Math.hypot(gh.x - p.x, gh.y - p.y);
    if (d < TILE * 0.65) {
      if (gh.scared) {
        gh.eaten = true;
        gh.scared = false;
        g.score += 200;
        audio.eat();
      } else {
        g.status = "dying";
        g.dyingTimer = 1400;
        audio.die();
      }
    }
  });

  // ---- Win check
  if (g.dotsRemaining <= 0) {
    g.status = "won";
    g.dyingTimer = 1800;
  }
}

// --- Cinematic intro storyboard ---------------------------------------------
// Runs on the canvas while the menu overlay is up. Loops a 4-scene story:
//   1) Duck sees a credit card
//   2) Duck chases the card across the screen
//   3) Villains materialize behind
//   4) "Insert Coin" flicker (arcade nostalgia)
function drawIntro(ctx, state, now) {
  const t = now * 0.001; // seconds
  const cycleT = t % 12; // 12s loop
  // Faded maze in the background (very subtle, so overlay text stays readable)
  ctx.fillStyle = "#04061a";
  ctx.fillRect(0, 0, W, H);
  ctx.globalAlpha = 0.18;
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      const ch = state.grid[y][x];
      if (ch === "#") {
        ctx.fillStyle = "#1e3a8a";
        ctx.fillRect(x * TILE + 2, y * TILE + 2, TILE - 4, TILE - 4);
      }
    }
  }
  ctx.globalAlpha = 1;

  // Scene picker (each scene = 3s)
  const scene = Math.floor(cycleT / 3);
  const localT = (cycleT % 3) / 3; // 0..1 within scene

  // Ground line the characters run on
  const groundY = H * 0.62;
  ctx.strokeStyle = "rgba(251, 191, 36, 0.35)";
  ctx.lineWidth = 2;
  ctx.setLineDash([6, 8]);
  ctx.beginPath();
  ctx.moveTo(24, groundY);
  ctx.lineTo(W - 24, groundY);
  ctx.stroke();
  ctx.setLineDash([]);

  if (scene === 0) {
    // Scene 1 — Duck spots credit card
    const duckX = W * 0.28;
    const cardX = W * 0.68 + Math.sin(t * 3) * 4;
    drawIntroCard(ctx, cardX, groundY - 24, t);
    drawIntroDuck(ctx, duckX, groundY - 12, "right", t, false);
    // Thought bubble "!"
    const bubbleX = duckX + 18, bubbleY = groundY - 40;
    ctx.fillStyle = "#fef3c7";
    ctx.beginPath();
    ctx.arc(bubbleX, bubbleY, 8 + Math.sin(t * 6) * 0.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#78350f";
    ctx.font = "bold 12px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("!", bubbleX, bubbleY + 4);
  } else if (scene === 1) {
    // Scene 2 — Duck chases card
    const dx = 40 + localT * (W - 120);
    const cx = 80 + localT * (W - 100);
    drawIntroCard(ctx, cx, groundY - 24 + Math.sin(t * 8) * 3, t);
    drawIntroDuck(ctx, dx, groundY - 12, "right", t, true);
    // Speed lines
    ctx.strokeStyle = "rgba(255,255,255,0.35)";
    ctx.lineWidth = 1.5;
    for (let i = 0; i < 3; i++) {
      const y = groundY - 8 - i * 6;
      const len = 20 + i * 8;
      ctx.beginPath();
      ctx.moveTo(dx - 28 - len, y);
      ctx.lineTo(dx - 28, y);
      ctx.stroke();
    }
  } else if (scene === 2) {
    // Scene 3 — Villains appear
    const duckX = W * 0.35;
    drawIntroDuck(ctx, duckX, groundY - 12, "right", t, true);
    VILLAINS.forEach((v, i) => {
      const gx = W * 0.85 - localT * 40 - i * 26;
      const gy = groundY - 14 + Math.sin(t * 4 + i) * 2;
      drawIntroGhost(ctx, gx, gy, v.color);
    });
    // Warning text
    ctx.fillStyle = "#fca5a5";
    ctx.font = "bold 11px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("⚠ CREDIT VILLAINS!", W / 2, groundY - 44);
  } else {
    // Scene 4 — "Insert Coin" flicker
    const flicker = Math.floor(t * 2) % 2 === 0 ? 1 : 0.15;
    ctx.globalAlpha = flicker;
    ctx.fillStyle = "#facc15";
    ctx.font = "900 22px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("PRESS START", W / 2, groundY - 10);
    ctx.globalAlpha = 1;
    // Small duck + villain still on ground
    drawIntroDuck(ctx, W * 0.3, groundY - 12, "right", t, false);
    drawIntroGhost(ctx, W * 0.7, groundY - 14, VILLAINS[0].color);
  }

  // Scan-line CRT effect
  ctx.globalAlpha = 0.05;
  ctx.fillStyle = "#000";
  for (let y = 0; y < H; y += 3) ctx.fillRect(0, y, W, 1);
  ctx.globalAlpha = 1;
}

function drawIntroCard(ctx, x, y, t) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(Math.sin(t * 2) * 0.08);
  // Card body
  const grad = ctx.createLinearGradient(-16, -10, 16, 10);
  grad.addColorStop(0, "#fef3c7");
  grad.addColorStop(0.5, "#f59e0b");
  grad.addColorStop(1, "#b45309");
  ctx.fillStyle = grad;
  ctx.strokeStyle = "#78350f";
  ctx.lineWidth = 1;
  roundRect(ctx, -18, -12, 36, 22, 3, true, true);
  // Chip
  ctx.fillStyle = "#fbbf24";
  roundRect(ctx, -12, -7, 8, 6, 1, true, false);
  // Stripe
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.fillRect(-14, 3, 26, 1.5);
  ctx.fillRect(-14, 6, 20, 1);
  // Sparkle
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(6, -4 + Math.sin(t * 5) * 1, 1.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawIntroDuck(ctx, x, y, dir, t, running) {
  const flip = dir === "left" ? -1 : 1;
  ctx.save();
  ctx.translate(x, y);
  const bob = running ? Math.abs(Math.sin(t * 12)) * -3 : Math.sin(t * 3) * -1;
  ctx.translate(0, bob);
  ctx.scale(flip, 1);
  // Feet (running cycle)
  ctx.fillStyle = "#ea580c";
  const feetPhase = running ? Math.sin(t * 18) : 0;
  ctx.beginPath();
  ctx.ellipse(-3, 12 + feetPhase * 2, 3, 1.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(4, 12 - feetPhase * 2, 3, 1.5, 0, 0, Math.PI * 2);
  ctx.fill();
  // Body
  ctx.fillStyle = "#facc15";
  ctx.beginPath();
  ctx.arc(0, 0, 11, 0, Math.PI * 2);
  ctx.fill();
  // Wing
  ctx.fillStyle = "#d97706";
  ctx.beginPath();
  ctx.ellipse(-3, 2, 4.5, 3, -0.3, 0, Math.PI * 2);
  ctx.fill();
  // Beak
  ctx.fillStyle = "#f97316";
  ctx.beginPath();
  ctx.moveTo(6, -1);
  ctx.lineTo(14, -3);
  ctx.lineTo(14, 3);
  ctx.closePath();
  ctx.fill();
  // Eye
  ctx.fillStyle = "#fff";
  ctx.beginPath(); ctx.arc(3, -4, 2.4, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#0f172a";
  ctx.beginPath(); ctx.arc(3.6, -4, 1.4, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
}

function drawIntroGhost(ctx, x, y, color) {
  ctx.save();
  ctx.translate(x, y);
  const r = 10;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(0, -2, r, Math.PI, 0);
  ctx.lineTo(r, r);
  const nH = 4;
  for (let i = nH; i > 0; i--) {
    const px = -r + ((2 * r) * (i - 1)) / nH;
    ctx.lineTo(px + r / nH, r - 3);
    ctx.lineTo(px, r);
  }
  ctx.closePath();
  ctx.fill();
  // Eyes
  ctx.fillStyle = "#fff";
  ctx.beginPath(); ctx.arc(-3, -2, 2.4, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(3, -2, 2.4, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#0f172a";
  ctx.beginPath(); ctx.arc(-2.5, -2, 1.2, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(3.5, -2, 1.2, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
}

function roundRect(ctx, x, y, w, h, r, fill, stroke) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  if (fill) ctx.fill();
  if (stroke) ctx.stroke();
}

// --- Rendering --------------------------------------------------------------
function draw(ctx, state, duckImg, ghostBlink) {
  ctx.fillStyle = "#04061a";
  ctx.fillRect(0, 0, W, H);

  // Maze walls & dots
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      const ch = state.grid[y][x];
      const cx = x * TILE + TILE / 2;
      const cy = y * TILE + TILE / 2;
      if (ch === "#") {
        // Deep blue rounded wall block with inner highlight
        ctx.fillStyle = "#1e3a8a";
        ctx.fillRect(x * TILE + 2, y * TILE + 2, TILE - 4, TILE - 4);
        ctx.strokeStyle = "#3b82f6";
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x * TILE + 3, y * TILE + 3, TILE - 6, TILE - 6);
      } else if (ch === "=") {
        // Ghost door
        ctx.fillStyle = "#fca5a5";
        ctx.fillRect(x * TILE, y * TILE + TILE / 2 - 2, TILE, 4);
      } else if (ch === ".") {
        ctx.fillStyle = "#fef3c7";
        ctx.beginPath();
        ctx.arc(cx, cy, 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (ch === "o") {
        // Power pellet — animated credit card
        drawCreditCardPellet(ctx, cx, cy, state.tick);
      }
    }
  }

  // Ghosts
  state.ghosts.forEach(gh => drawGhost(ctx, gh, state.powerTimer, ghostBlink));

  // Player
  drawPlayer(ctx, state.player, state.status, duckImg);

  // Level text
  ctx.fillStyle = "rgba(255,255,255,0.15)";
  ctx.font = "bold 40px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(`LV ${state.level}`, W / 2, H / 2 + 14);
}

function drawCreditCardPellet(ctx, cx, cy, tick) {
  const w = 18, h = 12;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(Math.sin(tick * 0.003) * 0.18);
  const bob = Math.sin(tick * 0.006) * 1.2;
  ctx.translate(0, bob);
  // Card body with metallic gradient
  const grad = ctx.createLinearGradient(-w / 2, -h / 2, w / 2, h / 2);
  grad.addColorStop(0, "#fef3c7");
  grad.addColorStop(0.45, "#fbbf24");
  grad.addColorStop(1, "#b45309");
  ctx.fillStyle = grad;
  ctx.strokeStyle = "#78350f";
  ctx.lineWidth = 0.8;
  roundRect(ctx, -w / 2, -h / 2, w, h, 1.6, true, true);
  // Chip
  ctx.fillStyle = "#eab308";
  ctx.fillRect(-w / 2 + 2, -h / 2 + 2, 4, 3);
  ctx.strokeStyle = "#78350f";
  ctx.lineWidth = 0.4;
  ctx.strokeRect(-w / 2 + 2, -h / 2 + 2, 4, 3);
  // Number stripes
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.fillRect(-w / 2 + 2, 1, w - 4, 1);
  ctx.fillRect(-w / 2 + 2, 3, w - 6, 0.7);
  // Sparkle shine that slides across every ~1.4s
  const shine = ((tick * 0.001) % 1.4) / 1.4;
  ctx.globalAlpha = 0.7;
  ctx.fillStyle = "#fff";
  ctx.fillRect(-w / 2 + shine * w - 2, -h / 2, 2, h);
  ctx.globalAlpha = 1;
  ctx.restore();
}

function drawGhost(ctx, gh, powerTimer, blink) {
  const r = TILE * 0.42;
  const x = gh.x, y = gh.y;
  const color = gh.eaten
    ? "rgba(255,255,255,0.15)"
    : gh.scared
      ? (powerTimer < 2000 && blink ? "#fff" : "#1d4ed8")
      : gh.color;
  // Body: bell shape
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y - 2, r, Math.PI, 0);
  ctx.lineTo(x + r, y + r);
  const nH = 4;
  for (let i = nH; i > 0; i--) {
    const px1 = x - r + ((2 * r) * (i - 1)) / nH;
    ctx.lineTo(px1 + r / nH, y + r - 3);
    ctx.lineTo(px1, y + r);
  }
  ctx.closePath();
  ctx.fill();

  if (gh.eaten) return;

  // Eyes (baseline)
  const eyeR = 3;
  const eyeOff = 4;
  const pupilOff = 1.5 * (DIRS[gh.dir]?.dx || 0);
  const pupilY = 1.5 * (DIRS[gh.dir]?.dy || 0);
  ctx.fillStyle = "#fff";
  ctx.beginPath(); ctx.arc(x - eyeOff, y - 2, eyeR, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(x + eyeOff, y - 2, eyeR, 0, Math.PI * 2); ctx.fill();
  if (gh.scared) {
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1.3;
    ctx.beginPath();
    ctx.moveTo(x - 6, y + 4); ctx.lineTo(x - 3, y + 2);
    ctx.moveTo(x - 3, y + 2); ctx.lineTo(x + 0, y + 4);
    ctx.moveTo(x + 0, y + 4); ctx.lineTo(x + 3, y + 2);
    ctx.moveTo(x + 3, y + 2); ctx.lineTo(x + 6, y + 4);
    ctx.stroke();
    return;
  }
  ctx.fillStyle = "#0f172a";
  ctx.beginPath(); ctx.arc(x - eyeOff + pupilOff, y - 2 + pupilY, 1.4, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(x + eyeOff + pupilOff, y - 2 + pupilY, 1.4, 0, Math.PI * 2); ctx.fill();

  // Outfit accessories on top of the base ghost
  drawGhostOutfit(ctx, gh, x, y, r);
}

function drawGhostOutfit(ctx, gh, x, y, r) {
  const outfit = gh.outfit || "banker";
  if (outfit === "banker") {
    // Suit vibes — top hat + bow tie
    ctx.fillStyle = "#0f172a";
    ctx.fillRect(x - 5, y - r - 6, 10, 5);  // hat crown
    ctx.fillRect(x - 7, y - r - 2, 14, 1.5); // brim
    // Hat band
    ctx.fillStyle = "#dc2626";
    ctx.fillRect(x - 5, y - r - 3, 10, 1);
    // Bow tie (below eyes)
    ctx.fillStyle = "#0f172a";
    ctx.beginPath();
    ctx.moveTo(x - 4, y + 5);
    ctx.lineTo(x, y + 3);
    ctx.lineTo(x + 4, y + 5);
    ctx.lineTo(x + 4, y + 8);
    ctx.lineTo(x, y + 6);
    ctx.lineTo(x - 4, y + 8);
    ctx.closePath();
    ctx.fill();
  } else if (outfit === "hipster") {
    // Beanie + chunky glasses + mustache
    ctx.fillStyle = "#7c3aed";
    // Beanie main
    ctx.fillRect(x - r + 2, y - r - 3, r * 2 - 4, 5);
    ctx.beginPath();
    ctx.arc(x, y - r - 3, r - 2, Math.PI, 0);
    ctx.fill();
    // Beanie cuff
    ctx.fillStyle = "#4c1d95";
    ctx.fillRect(x - r + 2, y - r + 1, r * 2 - 4, 2);
    // Pom-pom
    ctx.fillStyle = "#fbbf24";
    ctx.beginPath();
    ctx.arc(x, y - r - 6, 2, 0, Math.PI * 2);
    ctx.fill();
    // Chunky glasses (over eyes)
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(x - 4, y - 2, 3.5, 0, Math.PI * 2);
    ctx.arc(x + 4, y - 2, 3.5, 0, Math.PI * 2);
    ctx.moveTo(x - 0.5, y - 2);
    ctx.lineTo(x + 0.5, y - 2);
    ctx.stroke();
    // Mustache
    ctx.fillStyle = "#3f1d0a";
    ctx.beginPath();
    ctx.moveTo(x - 4, y + 5);
    ctx.quadraticCurveTo(x - 2, y + 3, x, y + 5);
    ctx.quadraticCurveTo(x + 2, y + 3, x + 4, y + 5);
    ctx.quadraticCurveTo(x + 2, y + 7, x, y + 5);
    ctx.quadraticCurveTo(x - 2, y + 7, x - 4, y + 5);
    ctx.fill();
  } else if (outfit === "rapper") {
    // Backwards cap + chain + shades
    ctx.fillStyle = "#000";
    // Cap crown
    ctx.fillRect(x - r + 1, y - r - 3, r * 2 - 2, 5);
    ctx.beginPath();
    ctx.arc(x, y - r - 3, r - 1, Math.PI, 0);
    ctx.fill();
    // Cap logo
    ctx.fillStyle = "#facc15";
    ctx.font = "bold 5px system-ui";
    ctx.textAlign = "center";
    ctx.fillText("CQ", x, y - r - 3);
    // Shades — one long block covering both eyes
    ctx.fillStyle = "#0f172a";
    roundRect(ctx, x - 8, y - 4, 16, 4, 1.5, true, false);
    // Nose bridge highlight
    ctx.fillStyle = "#22d3ee";
    ctx.fillRect(x - 0.5, y - 4, 1, 4);
    // Gold chain (below mouth)
    ctx.strokeStyle = "#fbbf24";
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(x, y + 3, 4.5, 0.15 * Math.PI, 0.85 * Math.PI);
    ctx.stroke();
    // Chain medallion
    ctx.fillStyle = "#facc15";
    ctx.beginPath();
    ctx.arc(x, y + 8, 2, 0, Math.PI * 2);
    ctx.fill();
  } else if (outfit === "beverly") {
    // Big curly hair + cat-eye sunnies + pearls
    ctx.fillStyle = "#a16207";
    // Big hair poof
    for (let i = 0; i < 5; i++) {
      const px = x - 6 + i * 3;
      ctx.beginPath();
      ctx.arc(px, y - r - 2, 3.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.beginPath();
    ctx.arc(x - 8, y - r + 2, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x + 8, y - r + 2, 3, 0, Math.PI * 2);
    ctx.fill();
    // Cat-eye sunglasses
    ctx.fillStyle = "#0f172a";
    ctx.beginPath();
    ctx.moveTo(x - 8, y - 3);
    ctx.lineTo(x - 6, y - 5);
    ctx.lineTo(x - 1, y - 3);
    ctx.lineTo(x - 1, y);
    ctx.lineTo(x - 7, y);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x + 8, y - 3);
    ctx.lineTo(x + 6, y - 5);
    ctx.lineTo(x + 1, y - 3);
    ctx.lineTo(x + 1, y);
    ctx.lineTo(x + 7, y);
    ctx.closePath();
    ctx.fill();
    // Pearl necklace
    ctx.fillStyle = "#fff";
    for (let i = -3; i <= 3; i++) {
      ctx.beginPath();
      ctx.arc(x + i * 1.8, y + 5 + Math.abs(i) * 0.3, 0.9, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function drawPlayer(ctx, p, status) {
  const r = TILE * 0.48;
  ctx.save();
  ctx.translate(p.x, p.y);
  const rot = { right: 0, down: Math.PI / 2, left: Math.PI, up: -Math.PI / 2 }[p.dir] || 0;
  ctx.rotate(rot);

  // Feet — running cycle, alternating waddle
  const feetPhase = Math.sin(p.mouth * 2) * 2;
  ctx.fillStyle = "#ea580c";
  ctx.beginPath();
  ctx.ellipse(-r * 0.3, r * 0.85 + feetPhase, 2.6, 1.4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(r * 0.2, r * 0.85 - feetPhase, 2.6, 1.4, 0, 0, Math.PI * 2);
  ctx.fill();

  // Body (yellow duck head/body — with mouth wedge that opens with stride)
  const mouth = 0.08 + (Math.sin(p.mouth) + 1) * 0.19;
  ctx.fillStyle = "#facc15";
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.arc(0, 0, r, mouth, Math.PI * 2 - mouth);
  ctx.closePath();
  ctx.fill();

  // Belly cream highlight
  ctx.fillStyle = "rgba(254, 249, 195, 0.6)";
  ctx.beginPath();
  ctx.arc(0, r * 0.15, r * 0.6, 0.3 * Math.PI, 0.7 * Math.PI);
  ctx.fill();

  // Wing (visible on the back — rotates with sprite)
  ctx.fillStyle = "#d97706";
  ctx.beginPath();
  ctx.ellipse(-r * 0.15, r * 0.05, r * 0.35, r * 0.22, -0.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#78350f";
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(-r * 0.4, r * 0.1);
  ctx.quadraticCurveTo(-r * 0.2, r * 0.25, r * 0.1, r * 0.15);
  ctx.stroke();

  // Orange beak inside the mouth wedge (deeper color when open)
  ctx.fillStyle = "#f97316";
  ctx.beginPath();
  ctx.moveTo(r * 0.5, 0);
  ctx.arc(0, 0, r * 0.92, mouth * 0.7, mouth);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(r * 0.5, 0);
  ctx.arc(0, 0, r * 0.92, Math.PI * 2 - mouth, Math.PI * 2 - mouth * 0.7);
  ctx.closePath();
  ctx.fill();
  // Inner dark mouth reveal
  if (mouth > 0.28) {
    ctx.fillStyle = "#7c2d12";
    ctx.beginPath();
    ctx.moveTo(r * 0.35, 0);
    ctx.arc(0, 0, r * 0.7, mouth * 0.5, mouth);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(r * 0.35, 0);
    ctx.arc(0, 0, r * 0.7, Math.PI * 2 - mouth, Math.PI * 2 - mouth * 0.5);
    ctx.closePath();
    ctx.fill();
  }

  // Eye — always upright regardless of direction
  ctx.rotate(-rot);
  const eyeX = 0, eyeY = -r * 0.55;
  ctx.fillStyle = "#fff";
  ctx.beginPath(); ctx.arc(eyeX, eyeY, 2.6, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = "#0f172a";
  ctx.lineWidth = 0.5;
  ctx.stroke();
  ctx.fillStyle = "#0f172a";
  ctx.beginPath(); ctx.arc(eyeX + 0.4, eyeY + 0.2, 1.5, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.beginPath(); ctx.arc(eyeX + 0.9, eyeY - 0.3, 0.6, 0, Math.PI * 2); ctx.fill();

  // Red CQ bib
  ctx.fillStyle = "#dc2626";
  ctx.beginPath();
  ctx.moveTo(-r * 0.55, r * 0.2);
  ctx.quadraticCurveTo(0, r * 0.75, r * 0.55, r * 0.2);
  ctx.quadraticCurveTo(r * 0.35, r * 0.5, 0, r * 0.55);
  ctx.quadraticCurveTo(-r * 0.35, r * 0.5, -r * 0.55, r * 0.2);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "#7f1d1d";
  ctx.lineWidth = 0.5;
  ctx.stroke();
  ctx.fillStyle = "#fff";
  ctx.font = "bold 7px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("CQ", 0, r * 0.42);

  ctx.restore();
}

// --- React component --------------------------------------------------------
export default function QuackManGame() {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const audioRef = useRef(null);
  const rafRef = useRef(0);
  const [ui, setUi] = useState({ score: 0, lives: 3, level: 1, status: "menu", highScore: 0 });
  const [muted, setMuted] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [nameInput, setNameInput] = useState("");
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [intro, setIntro] = useState({ active: false, frame: 0 });

  // Load leaderboard on mount
  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      const r = await api.get("/quackman/scores");
      setLeaderboard(r.data?.scores || []);
    } catch {}
  };

  const submitScore = async () => {
    const name = (nameInput || "").trim();
    if (!name) { setSubmitErr("Please enter a name."); return; }
    setSubmitBusy(true);
    setSubmitErr("");
    try {
      const r = await api.post("/quackman/scores", { name, score: ui.score, level: ui.level });
      setLeaderboard(r.data?.scores || []);
      setSubmitted(true);
      try { localStorage.setItem("cq_qm_name", name); } catch {}
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not submit. Try a different name.";
      setSubmitErr(typeof msg === "string" ? msg : "Could not submit.");
    } finally {
      setSubmitBusy(false);
    }
  };

  useEffect(() => {
    audioRef.current = new GameAudio();
    stateRef.current = makeState();
    stateRef.current.dotsRemaining = countDots(stateRef.current);
    setUi(u => ({ ...u, highScore: stateRef.current.highScore }));

    // Debug hook — lets automated tests jump to game over immediately.
    // Gated to non-production builds so it doesn't ship to real users.
    if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
      window.__qmForceGameOver = (score = 500) => {
        const s = stateRef.current;
        if (!s) return;
        s.score = score;
        s.lives = 0;
        s.status = "gameover";
        if (s.score > s.highScore) {
          s.highScore = s.score;
          try { localStorage.setItem("cq_qm_high", String(s.score)); } catch {}
        }
        setUi({ score: s.score, lives: 0, level: s.level, status: "gameover", highScore: s.highScore });
      };
    }

    let lastT = performance.now();
    const blink = { on: false, tAcc: 0 };
    const lastUiSync = [0];
    const loop = (now) => {
      const dt = Math.min(48, now - lastT);
      lastT = now;
      const s = stateRef.current;
      if (s.status === "playing") {
        step(s, dt, audioRef.current);
      } else if (s.status === "dying") {
        s.dyingTimer -= dt;
        if (s.dyingTimer <= 0) {
          s.lives -= 1;
          if (s.lives <= 0) {
            s.status = "gameover";
            if (s.score > s.highScore) {
              s.highScore = s.score;
              try { localStorage.setItem("cq_qm_high", String(s.score)); } catch {}
            }
          } else {
            resetPositions(s);
            s.status = "playing";
          }
          setUi({ score: s.score, lives: s.lives, level: s.level, status: s.status, highScore: s.highScore });
        }
      } else if (s.status === "won") {
        s.dyingTimer -= dt;
        if (s.dyingTimer <= 0) {
          s.level += 1;
          resetLevel(s);
          s.status = "playing";
          setUi({ score: s.score, lives: s.lives, level: s.level, status: s.status, highScore: s.highScore });
        }
      }
      // Sync UI (throttled ~5Hz to avoid re-render hitches at 60fps)
      blink.tAcc += dt;
      if (blink.tAcc > 220) { blink.on = !blink.on; blink.tAcc = 0; }
      const uiSyncGap = now - (lastUiSync[0] || 0);
      if (s.status === "playing" && uiSyncGap > 200) {
        lastUiSync[0] = now;
        setUi(u => (u.score !== s.score || u.lives !== s.lives ? { score: s.score, lives: s.lives, level: s.level, status: s.status, highScore: s.highScore } : u));
      }
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (s.status === "menu") {
          drawIntro(ctx, s, now);
        } else {
          draw(ctx, s, null, blink.on);
        }
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const dir = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right",
                    w: "up", W: "up", s: "down", S: "down", a: "left", A: "left", d: "right", D: "right" }[e.key];
      const s = stateRef.current;
      if (!s) return;
      if (dir) { s.player.nextDir = dir; e.preventDefault(); }
      if ((e.key === " " || e.key === "Enter") && (s.status === "menu" || s.status === "gameover" || s.status === "paused")) {
        handleStart();
        e.preventDefault();
      } else if ((e.key === "p" || e.key === "P") && s.status === "playing") {
        s.status = "paused"; setUi(u => ({ ...u, status: "paused" }));
      } else if ((e.key === "p" || e.key === "P") && s.status === "paused") {
        s.status = "playing"; setUi(u => ({ ...u, status: "playing" }));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [muted]);

  const handleStart = async () => {
    if (!muted) await audioRef.current?.unlock();
    const s = stateRef.current;
    if (s.status === "menu" || s.status === "gameover") {
      s.grid = MAZE.map(r => r.split(""));
      for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) if (s.grid[y][x] === "P") s.grid[y][x] = ".";
      s.score = 0;
      s.lives = 3;
      s.level = 1;
      s.dotsRemaining = countDots(s);
      resetPositions(s);
      setSubmitted(false);
      setSubmitErr("");
      // Pre-fill name from prior session
      try { const prev = localStorage.getItem("cq_qm_name"); if (prev && !nameInput) setNameInput(prev); } catch {}
      audioRef.current?.start();
    }
    s.status = "playing";
    setUi({ score: s.score, lives: s.lives, level: s.level, status: s.status, highScore: s.highScore });
  };

  const handleReset = () => {
    const s = stateRef.current;
    s.status = "menu";
    s.score = 0;
    s.lives = 3;
    s.level = 1;
    s.grid = MAZE.map(r => r.split(""));
    for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) if (s.grid[y][x] === "P") s.grid[y][x] = ".";
    s.dotsRemaining = countDots(s);
    resetPositions(s);
    setUi({ score: 0, lives: 3, level: 1, status: "menu", highScore: s.highScore });
  };

  const setDir = (dir) => {
    const s = stateRef.current;
    if (!s) return;
    s.player.nextDir = dir;
    if (s.status === "menu" || s.status === "gameover") handleStart();
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (audioRef.current) audioRef.current.enabled = !next;
  };

  const overlay = ui.status === "menu" || ui.status === "gameover" || ui.status === "paused" || ui.status === "won";

  return (
    <div className="cq-quackman-wrap w-full max-w-4xl mx-auto" data-testid="quackman-game">
      {/* Cabinet */}
      <div className="cq-quackman-cabinet rounded-[28px] p-3 sm:p-5 bg-gradient-to-br from-slate-900 via-slate-950 to-black border border-amber-400/25 shadow-[0_20px_60px_-20px_rgba(251,191,36,0.35)]">
        {/* HUD */}
        <div className="flex items-center justify-between gap-3 mb-3 text-white">
          <div className="flex items-center gap-4 sm:gap-6">
            <div>
              <p className="text-[9px] uppercase tracking-widest text-amber-300 font-bold">Score</p>
              <p className="font-mono font-black text-lg sm:text-xl" data-testid="qm-score">{ui.score.toString().padStart(5, "0")}</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-emerald-300 font-bold flex items-center gap-1"><Trophy size={10} /> High</p>
              <p className="font-mono font-black text-lg sm:text-xl">{ui.highScore.toString().padStart(5, "0")}</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-sky-300 font-bold">Level</p>
              <p className="font-mono font-black text-lg sm:text-xl">{ui.level}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1" aria-label="Lives">
              {[...Array(Math.max(0, ui.lives))].map((_, i) => (
                <Heart key={i} size={16} className="text-red-400 fill-red-500" data-testid={`qm-life-${i}`} />
              ))}
            </div>
            <button
              onClick={toggleMute}
              aria-label={muted ? "Unmute" : "Mute"}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
              data-testid="qm-mute"
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            <button
              onClick={handleReset}
              aria-label="Restart"
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
              data-testid="qm-reset"
            >
              <RotateCcw size={16} />
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div className="relative mx-auto" style={{ width: "100%", maxWidth: `${W}px` }}>
          <canvas
            ref={canvasRef}
            width={W}
            height={H}
            className="w-full h-auto rounded-2xl border border-amber-400/30 bg-[#04061a] cq-quackman-canvas"
            data-testid="qm-canvas"
          />
          {overlay && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm rounded-2xl text-center px-4"
              data-testid={`qm-overlay-${ui.status}`}
            >
              {ui.status === "menu" && (
                <div className="w-full max-w-md text-center">
                  <p className="text-amber-300 font-bold text-xs uppercase tracking-[0.28em]">Arcade mini-game</p>
                  <h3 className="font-display text-3xl sm:text-4xl font-black text-white mt-2 cq-qm-title">QuackMan</h3>
                  <p className="text-slate-300 text-sm mt-2 max-w-sm mx-auto">Chomp every dot. Dodge <span className="text-red-400 font-bold">Debt</span>, <span className="text-pink-400 font-bold">Utilization</span>, <span className="text-cyan-300 font-bold">Late Fee</span> &amp; <span className="text-amber-400 font-bold">Inquiry</span>. Grab a golden pellet to eat them back.</p>
                  {leaderboard.length > 0 && (
                    <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-3 text-left" data-testid="qm-menu-leaderboard">
                      <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1 mb-2 justify-center"><Trophy size={11} /> Global Top Scores</p>
                      <ol className="space-y-1">
                        {leaderboard.slice(0, 5).map((s, i) => (
                          <li key={i} className="flex items-center justify-between text-xs font-mono">
                            <span className="text-slate-400 w-5">#{i + 1}</span>
                            <span className={`flex-1 font-black ${i === 0 ? "text-amber-300" : "text-white"}`}>{s.name}</span>
                            <span className="text-slate-300">LV {s.level}</span>
                            <span className={`ml-3 font-bold w-16 text-right ${i === 0 ? "text-amber-300" : "text-white"}`}>{s.score.toLocaleString()}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                  <button onClick={handleStart} className="mt-5 inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black h-12 px-6 rounded-full transition shadow-lg shadow-amber-500/40" data-testid="qm-start">
                    <Play size={16} /> Start Game
                  </button>
                  <p className="text-slate-400 text-[11px] mt-3">Arrow keys / WASD · Space to start · P to pause</p>
                </div>
              )}
              {ui.status === "paused" && (
                <>
                  <h3 className="font-display text-3xl font-black text-white">Paused</h3>
                  <button onClick={handleStart} className="mt-4 inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black h-11 px-5 rounded-full" data-testid="qm-resume">
                    <Play size={14} /> Resume
                  </button>
                </>
              )}
              {ui.status === "won" && (
                <>
                  <p className="text-emerald-300 font-bold text-xs uppercase tracking-[0.28em]">Level Cleared</p>
                  <h3 className="font-display text-3xl font-black text-white mt-1">Nice waddle!</h3>
                  <p className="text-slate-300 text-sm mt-2">Next level loading…</p>
                </>
              )}
              {ui.status === "gameover" && (
                <div className="w-full max-w-md">
                  <p className="text-red-400 font-bold text-xs uppercase tracking-[0.28em] text-center">Game Over</p>
                  <h3 className="font-display text-3xl font-black text-white mt-1 text-center">Final Score {ui.score}</h3>
                  {ui.score > 0 && ui.score >= ui.highScore && (
                    <p className="text-amber-300 font-bold text-sm mt-1 text-center">🏆 New high score!</p>
                  )}

                  {/* Name entry — only if score > 0 and not yet submitted */}
                  {ui.score > 0 && !submitted && (
                    <div className="mt-4">
                      <label className="block text-[10px] uppercase tracking-widest text-slate-300 font-bold mb-1">Add to leaderboard</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={nameInput}
                          onChange={(e) => { setNameInput(e.target.value.toUpperCase().slice(0, 12)); setSubmitErr(""); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitScore(); } }}
                          placeholder="YOUR NAME"
                          maxLength={12}
                          className="flex-1 h-11 rounded-lg bg-white/10 border border-white/20 px-3 text-white font-mono font-bold placeholder:text-slate-500 focus:outline-none focus:border-amber-400"
                          data-testid="qm-name-input"
                          autoFocus
                        />
                        <button
                          onClick={submitScore}
                          disabled={submitBusy || !nameInput.trim()}
                          className="h-11 px-4 rounded-lg bg-amber-400 hover:bg-amber-300 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 font-black flex items-center gap-1 transition"
                          data-testid="qm-name-submit"
                        >
                          {submitBusy ? "..." : <><Send size={14} /> Save</>}
                        </button>
                      </div>
                      {submitErr && (
                        <p className="text-red-300 text-xs mt-2 flex items-center gap-1" data-testid="qm-name-error">
                          <AlertCircle size={12} /> {submitErr}
                        </p>
                      )}
                    </div>
                  )}
                  {submitted && (
                    <p className="text-emerald-300 text-sm text-center mt-3 font-bold" data-testid="qm-submitted">✓ Saved to the leaderboard</p>
                  )}

                  {/* Top 5 leaderboard preview */}
                  {leaderboard.length > 0 && (
                    <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-3" data-testid="qm-leaderboard">
                      <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1 mb-2"><Trophy size={11} /> Top Scores</p>
                      <ol className="space-y-1">
                        {leaderboard.slice(0, 5).map((s, i) => (
                          <li key={i} className="flex items-center justify-between text-xs font-mono">
                            <span className="text-slate-400 w-5">#{i + 1}</span>
                            <span className={`flex-1 font-black ${i === 0 ? "text-amber-300" : "text-white"}`}>{s.name}</span>
                            <span className="text-slate-300">LV {s.level}</span>
                            <span className={`ml-3 font-bold w-16 text-right ${i === 0 ? "text-amber-300" : "text-white"}`}>{s.score.toLocaleString()}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  <div className="mt-5 text-center">
                    <button onClick={handleStart} className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black h-12 px-6 rounded-full transition" data-testid="qm-restart">
                      <Play size={16} /> Play Again
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile D-pad */}
        <div className="mt-4 grid grid-cols-3 gap-2 max-w-[220px] mx-auto sm:hidden select-none" data-testid="qm-dpad">
          <div />
          <button aria-label="Up" onPointerDown={(e) => { e.preventDefault(); setDir("up"); }} className="h-12 rounded-xl bg-white/10 text-white text-xl font-black active:bg-amber-400 active:text-slate-950" data-testid="qm-dpad-up">▲</button>
          <div />
          <button aria-label="Left" onPointerDown={(e) => { e.preventDefault(); setDir("left"); }} className="h-12 rounded-xl bg-white/10 text-white text-xl font-black active:bg-amber-400 active:text-slate-950" data-testid="qm-dpad-left">◀</button>
          <div className="h-12 rounded-xl bg-slate-800/60" />
          <button aria-label="Right" onPointerDown={(e) => { e.preventDefault(); setDir("right"); }} className="h-12 rounded-xl bg-white/10 text-white text-xl font-black active:bg-amber-400 active:text-slate-950" data-testid="qm-dpad-right">▶</button>
          <div />
          <button aria-label="Down" onPointerDown={(e) => { e.preventDefault(); setDir("down"); }} className="h-12 rounded-xl bg-white/10 text-white text-xl font-black active:bg-amber-400 active:text-slate-950" data-testid="qm-dpad-down">▼</button>
          <div />
        </div>
      </div>
    </div>
  );
}
